import React from 'react';
import { Link } from 'react-router-dom';
import { Eye, Edit2 } from 'lucide-react';
import { ProjectListItem } from '../../../types';
import Badge from '../../../components/ui/Badge';
import { projectStatusVariant } from '../../../utils/badgeVariants';

export const PROJECT_COLUMNS = (
  highlightedId: number | null,
  onView: (id: number) => void,
  onEdit: (id: number) => void,
) => [
  {
    id: 'id',
    label: 'ID',
    accessor: 'id',
    render: (p: ProjectListItem) => (
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{p.id}</span>
    ),
  },
  {
    id: 'name',
    label: 'Job Common Name',
    accessor: 'name',
    render: (p: ProjectListItem) => {
      const isHighlighted = highlightedId === p.id;
      return isHighlighted ? (
        <span
          data-project-id={p.id}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#15803d', fontWeight: 600, fontSize: 13 }}
        >
          <span style={{ width: 6, height: 6, background: '#22c55e', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1s infinite' }} />
          {p.name}
        </span>
      ) : (
        <span data-project-id={p.id} style={{ fontSize: 13, color: 'var(--text-primary)' }}>{p.name}</span>
      );
    },
  },
  {
    id: 'status',
    label: 'Status',
    accessor: 'status',
    render: (p: ProjectListItem) =>
      p.status ? (
        <Badge variant={projectStatusVariant(p.status)}>{p.status}</Badge>
      ) : (
        <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>-</span>
      ),
  },
  {
    id: 'start_date',
    label: 'Start Date',
    accessor: 'start_date',
    render: (p: ProjectListItem) => (
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {p.start_date ? new Date(p.start_date).toLocaleDateString() : '-'}
      </span>
    ),
  },
  {
    id: 'end_date',
    label: 'End Date',
    accessor: 'end_date',
    render: (p: ProjectListItem) => (
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {p.end_date ? new Date(p.end_date).toLocaleDateString() : '-'}
      </span>
    ),
  },
  {
    id: 'project_description',
    label: 'Project Description',
    accessor: 'project_description',
    render: (p: ProjectListItem) =>
      p.project_description ? (
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }} title={p.project_description}>
          {p.project_description.length > 50
            ? `${p.project_description.substring(0, 50)}...`
            : p.project_description}
        </span>
      ) : (
        <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>-</span>
      ),
  },
  {
    id: 'project_number',
    label: 'Job Code',
    accessor: 'project_number',
    render: (p: ProjectListItem) => (
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{p.project_number || '-'}</span>
    ),
  },
  {
    id: 'client_id',
    label: 'ClientID',
    accessor: 'client_id',
    render: (p: ProjectListItem) => (
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{p.customer?.client_id || '-'}</span>
    ),
  },
  {
    id: 'customer_name',
    label: 'Customer Name',
    accessor: 'customer_name',
    render: (p: ProjectListItem) => (
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{p.customer?.item || '-'}</span>
    ),
  },
  {
    id: 'deal',
    label: 'Deal',
    accessor: 'deal',
    render: (p: ProjectListItem) =>
      p.deal ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <span style={{ fontSize: 13, fontWeight: 500, color: 'var(--text-primary)' }}>{p.deal.name}</span>
          <span style={{ fontSize: 11, color: 'var(--text-tertiary)' }}>{p.deal.stage}</span>
        </div>
      ) : (
        <span style={{ fontSize: 13, color: 'var(--text-tertiary)' }}>-</span>
      ),
  },
  {
    id: 'board',
    label: 'Job Board',
    accessor: 'board',
    render: (p: ProjectListItem) => (
      <Link to={`/projects/${p.id}/board`} style={{ fontSize: 13, color: '#2563eb', textDecoration: 'underline' }}>
        Open Board
      </Link>
    ),
  },
  {
    id: 'cases',
    label: 'Project Cases',
    accessor: 'cases',
    render: (p: ProjectListItem) => (
      <Link to={`/cases?project_id=${p.id}`} style={{ fontSize: 13, color: '#2563eb', textDecoration: 'underline' }}>
        Project Cases
      </Link>
    ),
  },
  {
    id: 'drive',
    label: 'Drive',
    accessor: 'drive',
    render: (p: ProjectListItem) => (
      <a
        href={p.drive_folder_url ?? '#'}
        target="_blank"
        rel="noreferrer"
        style={{ fontSize: 13, color: '#2563eb', textDecoration: 'underline' }}
      >
        Link
      </a>
    ),
  },
  {
    id: 'created_at',
    label: 'Created',
    accessor: 'created_at',
    render: (p: ProjectListItem) => (
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {new Date(p.created_at).toLocaleString()}
      </span>
    ),
  },
  {
    id: 'updated_at',
    label: 'Updated',
    accessor: 'updated_at',
    render: (p: ProjectListItem) => (
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
        {p.updated_at ? new Date(p.updated_at).toLocaleString() : '-'}
      </span>
    ),
  },
  {
    id: 'actions',
    label: 'Actions',
    accessor: 'actions',
    render: (p: ProjectListItem) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={() => onView(p.id)}
          title="View details"
          style={{ padding: '6px', color: '#2563eb', background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'rgba(37,99,235,0.1)')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
        >
          <Eye style={{ width: 16, height: 16 }} />
        </button>
        <button
          onClick={() => onEdit(p.id)}
          title="Edit project"
          style={{ padding: '6px', color: '#16a34a', background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'rgba(22,163,74,0.1)')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
        >
          <Edit2 style={{ width: 16, height: 16 }} />
        </button>
      </div>
    ),
  },
];
